import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { map, mergeMap } from "rxjs/operators";
import { Observable, forkJoin, of } from 'rxjs';
import * as _ from 'lodash';
import { FileService, IDealComitteeBO, IDealComitteeEntraceBO } from '@mapfre-tech/amap3';

@Injectable({
  providedIn: 'root'
})

export class DealComitteeService {

  className = 'DealComitteeService'

  constructor(private http: HttpClient, private fileService: FileService) { }

  /*
  * Deal Comittees
  */
  getAllDealComittees(): Observable<IDealComitteeBO[]> {
    return this.http.get<IDealComitteeBO[]>(environment.base_URL + "/deal-comittees")
  }

  getDealComitteeById(id: number): Observable<IDealComitteeBO> {
    return this.http.get<IDealComitteeBO>(environment.base_URL + `/deal-comittees/${id}`);
  }

  newDealComittee(assessment: IDealComitteeBO): Observable<IDealComitteeBO[]> {
    return this.http.post<IDealComitteeBO[]>(environment.base_URL + "/deal-comittees", assessment);
  }

  newDealComitteeWithFiles(assessment: IDealComitteeBO): Observable<IDealComitteeBO[]> {
    // PRE FILES INI
    const filesToUpload = ((assessment.documents ?? []) as unknown[]).filter((f): f is File => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.newDealComittee(assessment).pipe(mergeMap((res: IDealComitteeBO[]) => {
        const entityId = String(res?.[0]?.idDealComitteePk ?? '');
        const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'dealComittee', entityId));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.newDealComittee(assessment)
    }
  }

  updateDealComittee(assessment: IDealComitteeBO, id: number): Observable<IDealComitteeBO[]> {
    return this.http.put<IDealComitteeBO[]>(environment.base_URL + `/deal-comittees/${id}`, assessment)
  }

  updateDealComitteeWithFiles(assessment: IDealComitteeBO, id: number): Observable<IDealComitteeBO[]> {
    // PRE FILES INI
    const tempList: any[] = [];
    _.forEach(_.get(assessment, 'documents'), (doc: any) => {
      if(!doc.idtSqn){
        tempList.push(doc)
      }
    })
    const filesToUpload = tempList.filter((f): f is File => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.updateDealComittee(assessment, id).pipe(mergeMap((res: IDealComitteeBO[]) => {
        const entityId = String(res?.[0]?.idDealComitteePk ?? id);
        const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'dealComittee', entityId));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.updateDealComittee(assessment, id);
    }
  }

  /**
   * Deletes a deal comittee. Its entraces are deleted first, since the API rejects deleting a comittee that still has entraces.
   */
  deleteDealComittee(id: number): Observable<IDealComitteeBO> {
    return this.getAllDealComitteesEntraces(id).pipe(
      mergeMap((entraces: IDealComitteeEntraceBO[]) => {
        const deletions = (entraces ?? []).map(entrace => this.deleteDealComitteeEntrace(id, entrace.idDealComitteeEntracePk as number));
        return deletions.length ? forkJoin(deletions) : of([]);
      }),
      mergeMap(() => this.http.delete<IDealComitteeBO>(environment.base_URL + `/deal-comittees/${id}`))
    );
  }

  /*
  * Deal Comittees Entrace
  */
  getAllDealComitteesEntraces(dealComitteeId: number): Observable<IDealComitteeEntraceBO[]> {
    return this.http.get<IDealComitteeEntraceBO[]>(environment.base_URL + `/deal-comittees/${dealComitteeId}/entrance`)
  }

  getDealComitteeEntraceById(dealComitteeId: number, dealComitteeEntraceId: number): Observable<IDealComitteeEntraceBO> {
    return this.http.get<IDealComitteeEntraceBO>(environment.base_URL + `/deal-comittees/${dealComitteeId}/entrance/${dealComitteeEntraceId}`);
  }

  newDealComitteeEntrace(assessment: IDealComitteeEntraceBO, dealComitteeId: number): Observable<IDealComitteeEntraceBO[]> {
    return this.http.post<IDealComitteeEntraceBO[]>(environment.base_URL + `/deal-comittees/${dealComitteeId}/entrance`, assessment);
  }

  newDealComitteeEntraceWithFiles(assessment: IDealComitteeEntraceBO, dealComitteeId: number): Observable<IDealComitteeEntraceBO[]> {
    // PRE FILES INI
    const filesToUpload = ((assessment.documents ?? []) as unknown[]).filter((f): f is File => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.newDealComitteeEntrace(assessment, dealComitteeId).pipe(mergeMap((res: any) => {
        const entityId = String(res?.[0]?.idDealComitteeEntracePk ?? '');
        const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'dealComitteeEntrace', entityId));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.newDealComitteeEntrace(assessment, dealComitteeId)
    }
  }

  updateDealComitteeEntrace(assessment: IDealComitteeEntraceBO, dealComitteeId: number, dealComitteeEntraceId: number): Observable<IDealComitteeEntraceBO[]> {
    return this.http.put<IDealComitteeEntraceBO[]>(environment.base_URL + `/deal-comittees/${dealComitteeId}/entrance/${dealComitteeEntraceId}`, assessment)
  }

  updateDealComitteeEntraceWithFiles(assessment: IDealComitteeEntraceBO, dealComitteeId: number, dealComitteeEntraceId: number): Observable<IDealComitteeEntraceBO[]> {
    // PRE FILES INI
    const tempList: any[] = [];
    _.forEach(_.get(assessment, 'documents'), (doc: any) => {
      if(!doc.idtSqn){
        tempList.push(doc)
      }
    })
    const filesToUpload = tempList.filter((f): f is File => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.updateDealComitteeEntrace(assessment, dealComitteeId, dealComitteeEntraceId).pipe(mergeMap((res: any) => {
        const entityId = String(res?.[0]?.idDealComitteeEntracePk ?? dealComitteeEntraceId);
        const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'dealComitteeEntrace', entityId));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.updateDealComitteeEntrace(assessment, dealComitteeId, dealComitteeEntraceId);
    }
  }

  deleteDealComitteeEntrace(dealComitteeId: number, dealComitteeEntraceId: number): Observable<IDealComitteeEntraceBO> {
    return this.http.delete<IDealComitteeEntraceBO>(environment.base_URL + `/deal-comittees/${dealComitteeId}/entrance/${dealComitteeEntraceId}`);
  }

}
